// Pure helpers for the campaign parameters editor
// (components/CampaignParamsAndPostbacks.jsx). Kept React-free so they are
// unit-tested directly.

// Keep URL keys safe so the copied tracking link never renders %20. Mirrors
// backend/models.py sanitize_param_key: trim, spaces -> "_", drop the rest.
export function sanitizeParamKey(key) {
  return String(key || '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_.-]/g, '');
}

// Shape used to compare the editor rows with what is saved on the campaign:
// rows without a key are dropped on save, empty fields equal missing ones.
export function normalizeParams(params) {
  return (params || [])
    .map((p) => ({
      name: String((p && p.name) || ''),
      key: sanitizeParamKey(p && p.key),
      placeholder: String((p && p.placeholder) || ''),
      slot: String((p && p.slot) || ''),
    }))
    .filter((p) => p.key);
}

// True when the editor holds parameter edits that are not saved yet.
export function paramsChanged(saved, current) {
  return JSON.stringify(normalizeParams(saved)) !== JSON.stringify(normalizeParams(current));
}

// Standard sub keys sub1…sub30.
const REPORT_KEY = /^sub([1-9]|[12][0-9]|30)$/;

export const SUB_SLOTS = Array.from({ length: 30 }, (_, i) => 'sub' + (i + 1));

// legacy-tracker named fields (backend/param_defaults.py FIELD_SLOTS).
export const FIELD_SLOTS = ['external_id', 'ad_campaign_id', 'creative_id', 'keyword', 'source', 'cost', 'currency'];

export const SLOT_OPTIONS = FIELD_SLOTS.concat(SUB_SLOTS);

// Slot for a legacy-tracker template field: sub_id_5 -> sub5, external_id as is.
export function slotForField(field) {
  const f = String(field || '').trim().toLowerCase();
  if (FIELD_SLOTS.includes(f)) return f;
  const m = /^sub(?:_id)?_?(\d{1,2})$/.exec(f);
  if (m && Number(m[1]) >= 1 && Number(m[1]) <= 30) return 'sub' + Number(m[1]);
  return '';
}
// Keys the tracker reads by name; they never belong in Sub-ID reports.
const SYSTEM_KEYS = new Set([
  'tm', 'tml', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid',
  'value', 'currency', 'cost', 'external_id', 'click_id',
]);

export function isReportKey(key) {
  return REPORT_KEY.test(String(key || ''));
}

// Declared keys that are stored on clicks and work as {key} macros but do not
// appear in Sub-ID reports (custom keys such as a, b, s, c).
export function nonReportKeys(params) {
  return normalizeParams(params)
    .map((p) => p.key)
    .filter((k) => !isReportKey(k) && !SYSTEM_KEYS.has(k.toLowerCase()));
}

// URL keys are case-sensitive. A key that is only a case variant of one the
// tracker reads by name (TM, Tml, Sub1) is never picked up.
export function miscasedKeys(params) {
  return normalizeParams(params)
    .map((p) => p.key)
    .filter((k) => {
      const lower = k.toLowerCase();
      return k !== lower && (lower === 'tm' || lower === 'tml' || isReportKey(lower));
    });
}

// Mirrors backend/param_defaults.py: an ad-network macro or a template sample
// is link text only and is not stored on clicks that lack the key.
export function isUnfilledMacro(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  return (
    /\{\{?[^{}\s]+\}\}?/.test(text)
    || /^(?:__[A-Za-z0-9_.]+__|\[[A-Za-z0-9_.:-]+\]|%[A-Za-z0-9_.]+%)$/.test(text)
    || /\bAW-X{3,}/i.test(text)
  );
}
